import React, {useMemo} from "react";
import {Steps} from "antd";
import {useTranslation} from "react-i18next";
import StepHeader from "./StepHeader";
import {useStore} from "../../../../../hooks/useStore";
import StepDescription from "./StepDescription";
import {StepTypes} from "./StepTypes";
import {Container, TrialStep, TrialStepContainer} from "./OrderStepsStyles";

const OrderSteps = ({orderData, transferData, renderProofIcon}: StepTypes) => {
    const stores = useStore();
    const {t} = useTranslation(["orderDetail"]);

    // delivery info
    const createdAt = orderData.data?.delivery.createdAt;
    const endDate = orderData.data?.steps?.slice(-1)[0]?.endDate;
    const currentDeliveryState = orderData?.data?.delivery?.deliveryState;
    const currentStepIndex = orderData.data.delivery.currentStepIndex;
    const steps = orderData?.data?.steps;
    const deliveryType = orderData?.data?.delivery?.deliveryType;
    const routes = orderData.data?.estimation?.routes;
    const summary = orderData.data?.delivery?.summary;

    const stepsData = useMemo(() => {
        if (["canceled", "rejected", "returned"].includes(currentDeliveryState)) {
            return steps?.slice(0, currentStepIndex);
        } else {
            return orderData?.data?.steps;
        }
    }, [currentStepIndex, currentDeliveryState, orderData?.data?.steps]);

    const currentStep = useMemo(
        () => {
            if (["delivered", "returned"].includes(currentDeliveryState)) {
                return stepsData?.length;
            }
            const lastStepWithEndDate = stepsData?.reduceRight((acc, step, index) => {
                if (acc === -1 && step.endDate) return index;
                return acc;
            }, -1);
            return lastStepWithEndDate !== -1 ? lastStepWithEndDate : -1;
        },
        /* endDate && ["delivered", "returned"].includes(currentDeliveryState)
                ? currentStepIndex + 1
                : currentStepIndex - 1, */
        [endDate, currentDeliveryState, currentStepIndex]
    );

    const getStepInfo = (step, index) => {
        const orderNumber = step.stepInfo.orderNumber;
        const activeLink = orderData.data.delivery.currentStepIndex <= index;
        const assignedCourierId = orderData.data.delivery.assignedCourier._id;

        const transferCouriers = transferData?.data
            ?.map(transferItem => [transferItem.fromCourier, transferItem.toCourier])
            .flat();

        const stepCourier = transferCouriers?.find(
            courier => (step.courierId || assignedCourierId) === courier._id
        );

        const assignedCourier =
            stepCourier?.fullname || orderData.data.delivery.assignedCourier.fullname;
        const courierLinkTo =
            stores.meStore.checkRole("courierDetail") && `/couriers/${assignedCourierId}`;

        return {
            state: step.deliveryState,
            stepInfoName: step.stepInfo?.name,
            cardType: step.cardType,
            _id: step.stepInfo?._id,
            stepIndex: step.stepIndex,
            //mergedAt: step.stepInfo?.mergedAt, //hide for customer
            startDate: step.startDate,
            endDate: step.endDate,
            passed: step.stepInfo.passed,
            canceled: step?.stepInfo?.canceled,
            orderNumber,
            courierLinkTo,
            assignedCourier,
            activeLink,
            trial: step.trial
        };
    };

    return (
        <Container>
            <Steps
                progressDot
                direction="vertical"
                current={currentStep}
                className="customer-order-steps"
            >
                {stepsData.map((step, index) => {
                    const info = getStepInfo(step, index);
                    return (
                        <React.Fragment key={index}>
                            <Steps.Step
                                data-testid="orderDetailCard_step"
                                data-testvalue={index}
                                key={index}
                                title={
                                    <StepHeader
                                        {...info}
                                        routes={routes}
                                        deliveryType={deliveryType}
                                    />
                                }
                                description={
                                    <StepDescription
                                        {...info}
                                        currentStepIndex={currentStepIndex}
                                        deliveryCreatedAt={createdAt}
                                        renderProofIcon={() => renderProofIcon(step)}
                                    />
                                }
                            />
                            {info.state === "onDeliveryAddress" && Boolean(info.trial) && (
                                <TrialStepContainer
                                    id={"trial-step"}
                                    iscompleted={Boolean(info.trial.endDate).toString()}
                                >
                                    <Steps progressDot direction="vertical" current={2}>
                                        <TrialStep
                                            data-testid="orderDetailCard_step"
                                            data-testvalue={index}
                                            key={index}
                                            icon={<React.Fragment />}
                                            title={
                                                <StepHeader
                                                    {...info}
                                                    isTrial
                                                    routes={routes}
                                                    deliveryType={deliveryType}
                                                />
                                            }
                                            description={
                                                <StepDescription
                                                    {...info.trial}
                                                    startDate={info.trial.startDate}
                                                    endDate={info.trial.endDate}
                                                    currentStepIndex={currentStepIndex}
                                                    deliveryCreatedAt={createdAt}
                                                    renderProofIcon={() => renderProofIcon(step)}
                                                />
                                            }
                                        />
                                        <Steps.Step style={{display: "none"}} />
                                    </Steps>
                                </TrialStepContainer>
                            )}
                        </React.Fragment>
                    );
                })}
                <Steps.Step
                    data-testid="orderDetailCard_step"
                    data-testvalue={"lastStep"}
                    title={
                        summary && summary?.types?.length !== 0
                            ? summary.types.map(
                                  type =>
                                      t(`deliveryInfo.summary.${type}`, {
                                          ns: "orderDetail",
                                          ordersCount: summary.ordersCount,
                                          deliveredOrders: summary.deliveredOrders,
                                          canceledOrders: summary.canceledOrders,
                                          canceledOrdersBeforePickup:
                                              summary.canceledOrdersBeforePickup,
                                          productsCount: summary.productsCount,
                                          trialProducts: summary.trialProducts,
                                          returnedTrialProducts: summary.returnedTrialProducts,
                                          returnedAssets: summary.returnedAssets,
                                          returnedProducts: summary.returnedProducts,
                                          deliveredProducts:
                                              summary.productsCount -
                                              summary.returnedProducts -
                                              summary.returnedAssets
                                      }) + " "
                              )
                            : t(`deliveryInfo.summary.deliveryCompleted`, {
                                  ns: "orderDetail"
                              })
                    }
                />
            </Steps>
        </Container>
    );
};

export default OrderSteps;
